import { useMemo } from "react";
import { useUser } from "../../../api/useAuth";
import { useRecords } from "../../../api/useRecords";
import { ROLES } from "../../../utils/constants";
import { useFieldSelectOptions } from "../../../utils/useFieldSelectOptions";
import { StaffAgreement, WorkLog } from "../types";
import { createTimeOffEntries } from "./general";
import { calculateHolidaysTotal, getCumulativeToDate } from "./calculations";
import { getCurrentRunStartDate } from "./employmentRun";
import { resolveUserSnapshotContext, StaffHoursSnapshot } from "./snapshots";
import { isRoleAdmin } from "../../../utils/rolesUtils";
import { endOfDay, max, min } from "date-fns";
import { getCumulativeStartDate } from "./constants";
import { getWeekEndDate } from "../../../utils/dateUtils";

export const useWorkLogs = (monthStartDate: Date, endDate: Date, userId?: string) => {
	// Extend to the week-aligned window end so a month-straddling boundary week (e.g. Mar 30–Apr 5) is
	// fully loaded for the cumulative breakdown. Monthly figures keep their own calendar-month filter.
	const stableUpperBound = useMemo(() => {
		const weekAlignedEnd = getCumulativeToDate(endDate);
		const currentWeekEnd = endOfDay(getWeekEndDate(new Date()));
		const upperBound = max([weekAlignedEnd, currentWeekEnd]);
		return upperBound.toISOString();
	}, [endDate.getTime()]);

	const stableMonthStart = useMemo(() => monthStartDate.toISOString(), [monthStartDate.getTime()]);
	const stableMonthEnd = useMemo(() => endDate.toISOString(), [endDate.getTime()]);

	const monthlyDateFilter = useMemo(() => ({
		start_date: { _gte: stableMonthStart, _lte: stableMonthEnd },
	}), [stableMonthStart, stableMonthEnd]);

	const technicianUser = useMemo(() =>
		userId ? { technician: { user_id: { id: { _eq: userId } } } } : {},
	[userId]);

	const user = useMemo(() =>
		userId ? { user: { id: { _eq: userId } } } : {},
	[userId]);

	const { data: currentUser } = useUser();

	const isCurrentUserAdmin =
		isRoleAdmin(currentUser?.role.id) ||
		currentUser?.role.id === ROLES.accounting ||
		currentUser?.role.id === ROLES.virtual_assistant;

	const staffAgreementFields = [
		"*.*",
		"user.id,user.role,user.first_name,user.last_name,user.avatar",
		"user.technician_id.specializations",
		"staff_weekly_hours.id",
		"staff_weekly_hours.date_start",
		"staff_weekly_hours.date_end",
		"staff_weekly_hours.weekly_hours",
		"staff_weekly_hours.day_off_type",
		"staff_weekly_hours.fixed_days_off",
		"staff_weekly_hours.agreement_type",
		"staff_weekly_hours.holidays_settlement",
		"files.*",
	].join(",");

	const {
		data: staffAgreementsRaw,
		status: staffAgreementStatus,
		error: staffAgreementError,
	} = useRecords("staff_agreements", staffAgreementFields, {
		extraOptions: {
			filter: {
				...user,
				status: { _eq: "active" },
			},
		},
	});

	const { data: snapshotsRaw, status: snapshotsStatus } = useRecords(
		"staff_hours_snapshots",
		"id,user,checkpoint_date,computed_worked,computed_expected,computed_balance,manual_balance,note",
		{
			extraOptions: {
				filter: { ...user },
				pageSize: -1,
			},
		}
	);

	const snapshotsByUser = useMemo(() => {
		const map = new Map<string, Array<StaffHoursSnapshot>>();
		if (snapshotsStatus !== "success") return map;
		(snapshotsRaw?.items || []).forEach((snapshot: any) => {
			const snapshotUserId = typeof snapshot.user === "object" ? snapshot.user?.id : snapshot.user;
			if (!snapshotUserId) return;
			const userSnapshots = map.get(snapshotUserId) || [];
			userSnapshots.push(snapshot);
			map.set(snapshotUserId, userSnapshots);
		});
		return map;
	}, [snapshotsRaw, snapshotsStatus]);

	// Fetch back to each user's snapshot window start (hard floor when unfrozen) — NOT the current run
	// start, because the weekly-detail modal lists the whole history across runs.
	const fetchWindowStart = useMemo(() => {
		const hardFloorStart = getCumulativeStartDate();
		if (staffAgreementStatus !== "success" || snapshotsStatus !== "success") return hardFloorStart;
		const agreements = staffAgreementsRaw?.items || [];
		if (agreements.length === 0) return hardFloorStart;
		const userStarts = agreements.map((agreement: any) => {
			const agreementUserId = agreement.user?.id;
			const currentRunStartDate = getCurrentRunStartDate(agreement.staff_weekly_hours || []);
			const { windowStart } = resolveUserSnapshotContext(snapshotsByUser.get(agreementUserId) || [], currentRunStartDate);
			return windowStart;
		});
		const earliestStart = min(userStarts);
		return earliestStart;
	}, [staffAgreementStatus, snapshotsStatus, staffAgreementsRaw, snapshotsByUser]);

	const stableFetchLowerBound = useMemo(() => {
		const fetchLowerBound = min([monthStartDate, fetchWindowStart]);
		return fetchLowerBound.toISOString();
	}, [monthStartDate.getTime(), fetchWindowStart]);

	const fullHistoryDateFilter = useMemo(() => ({
		start_date: {
			_gte: stableFetchLowerBound,
			_lte: stableUpperBound,
		},
	}), [stableFetchLowerBound, stableUpperBound]);

	const { data: sessions, status: sessionsStatus } = useRecords(
		"sessions",
		"id,start_date,end_date,title,technician.user_id.*",
		{
			extraOptions: {
				filter: {
					...monthlyDateFilter,
					...technicianUser,
					status: { _eq: "done" },
					service: { _nnull: true },
				},
				pageSize: -1,
			},
		}
	);

	const schedulesFields = [
		"id,start_date,end_date,notes,clinic_id",
		"user.id,user.role,user.first_name,user.last_name,user.avatar"
	].join(",")
	const { data: schedules, status: schedulesStatus } = useRecords(
		"schedules", schedulesFields,
		{
			extraOptions: {
				filter: {
					...fullHistoryDateFilter,
					...user,
				},
				pageSize: -1,
			},
		}
	);

	const { data: timeOffRaw, status: timeOffStatus } = useRecords("time_off", "*.*", {
		extraOptions: {
			filter: {
				...fullHistoryDateFilter,
				...user,
			},
			pageSize: -1,
		},
	});

	const timeOffOptions = useFieldSelectOptions("time_off", "type");

	const workLogs =
		sessionsStatus === "success" && schedulesStatus === "success"
			? createWorklogs(sessions.items, schedules.items)
			: [];

	const timeOff = timeOffStatus === "success" ? createTimeOffEntries(timeOffRaw.items) : [];

	const year = monthStartDate.getFullYear();
	const yearHolidayItems = (timeOffRaw?.items || []).filter((entry: any) =>
		entry.type === "holiday" &&
		entry.start_date >= `${year}-01-01` &&
		entry.start_date < `${year + 1}-01-01`
	);
	const thisYearHolidays = calculateHolidaysTotal(yearHolidayItems).totalHolidays;

	const staffAgreements: Array<StaffAgreement> = staffAgreementStatus === "success" ? staffAgreementsRaw.items : [];

	const activeAgreementUserIds = new Set(
		staffAgreements.map((agreement) => agreement.user?.id).filter(Boolean)
	);

	const filteredWorkLogs = workLogs.filter((log) => activeAgreementUserIds.has(log.user?.id));
	const filteredTimeOff = timeOff.filter((entry) => activeAgreementUserIds.has(entry.user?.id));


	const isLoading =
		sessionsStatus === "loading" ||
		schedulesStatus === "loading" ||
		timeOffStatus === "loading" ||
		staffAgreementStatus === "loading";
	const isError =
		sessionsStatus === "error" ||
		schedulesStatus === "error" ||
		timeOffStatus === "error" ||
		staffAgreementError;

	const allowedUserIds = schedulesStatus === "success"
		? new Set(schedules.items.map((schedule: any) => schedule.user?.id).filter(Boolean))
		: new Set();

	return {
		workLogs: filteredWorkLogs,
		timeOff: filteredTimeOff,
		timeOffTypes: timeOffOptions,
		thisYearHolidays,
		currentUser,
		isCurrentUserAdmin,
		staffAgreements,
		snapshotsByUser,
		allowedUserIds,
		isLoading,
		isError,
	};
};

export const createWorklogs = (sessions: any[], schedules: any[]): WorkLog[] => {
	const worklogs: WorkLog[] = [];

	sessions.forEach((session) => {
		worklogs.push({
			id: session.id,
			start_date: session.start_date,
			end_date: session.end_date,
			user: session.technician?.user_id,
			type: "clients",
		});
	});

	schedules.forEach((schedule) => {
		worklogs.push({
			id: schedule.id,
			start_date: schedule.start_date,
			end_date: schedule.end_date,
			user: schedule.user,
			type: "schedules",
			notes: schedule.notes,
			clinic_id: schedule.clinic_id,
		});
	});

	return worklogs;
};
